/**
 * TimezoneService
 *
 * Detects the user's IANA timezone by IP address (ipapi.co) and caches
 * the result for 24 hours using the app's storage abstraction.
 *
 * Falls back gracefully to Intl.DateTimeFormat if the network call fails
 * or the app is offline — so calendar always works.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const CACHE_KEY = 'agape_user_timezone';
const CACHE_TS_KEY = 'agape_user_timezone_ts';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// ── Storage helpers (same pattern as storage.ts) ──────────────────────────────

async function readCache(key: string): Promise<string | null> {
  if (Platform.OS === 'web') return localStorage.getItem(key);
  return AsyncStorage.getItem(key);
}

async function writeCache(key: string, value: string): Promise<void> {
  if (Platform.OS === 'web') {
    localStorage.setItem(key, value);
  } else {
    await AsyncStorage.setItem(key, value);
  }
}

// ── System fallback ───────────────────────────────────────────────────────────

function systemTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

// ── IP lookup ─────────────────────────────────────────────────────────────────

async function fetchTimezoneByIP(): Promise<string> {
  // ipapi.co is free, no API key required, ~150k req/month limit
  const response = await fetch('https://ipapi.co/timezone/', {
    method: 'GET',
    headers: { Accept: 'text/plain' },
  });
  if (!response.ok) throw new Error(`ipapi.co ${response.status}`);
  const tz = (await response.text()).trim();
  // Basic sanity check: IANA names contain "/"
  if (!tz || !tz.includes('/')) throw new Error(`Invalid tz: ${tz}`);
  return tz;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the user's IANA timezone string.
 * First call: fetches by IP and caches result.
 * Subsequent calls within 24h: returns cached value instantly.
 * On error: returns the device/browser timezone.
 */
export async function getUserTimezone(): Promise<string> {
  try {
    const cachedTz = await readCache(CACHE_KEY);
    const cachedTs = await readCache(CACHE_TS_KEY);

    if (cachedTz && cachedTs) {
      const age = Date.now() - parseInt(cachedTs, 10);
      if (age < CACHE_TTL_MS) return cachedTz;
    }

    const tz = await fetchTimezoneByIP();
    await writeCache(CACHE_KEY, tz);
    await writeCache(CACHE_TS_KEY, String(Date.now()));
    return tz;
  } catch {
    // Fail silently — use system timezone as fallback
    return systemTimezone();
  }
}

/**
 * Synchronous version — returns cached timezone or system fallback.
 * Safe to call in render; won't trigger a network request.
 */
export function getCachedTimezone(): string {
  if (Platform.OS === 'web') {
    return localStorage.getItem(CACHE_KEY) || systemTimezone();
  }
  // On native, AsyncStorage is always async, so fall back to system tz
  return systemTimezone();
}
